import { useState, useEffect } from 'react';
import { Input, Button, Tag, Tooltip, Typography, Popconfirm, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { EntityImage } from '../Entities/EntityImage';

const { Text } = Typography;

// Name field that saves on blur/Enter rather than per keystroke, and follows
// the server value when it changes underneath (another admin, a reload).
function GroupName({ value, onSave, disabled }) {
    const [draft, setDraft] = useState(value);
    useEffect(() => setDraft(value), [value]);
    const commit = () => {
        const name = draft.trim();
        if (!name) { setDraft(value); return; }
        if (name !== value) onSave(name);
    };
    return (
        <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onPressEnter={e => e.target.blur()}
            variant="borderless"
            className="gathering-group-name"
            maxLength={80}
            disabled={disabled}
        />
    );
}

/**
 * Admin: the groups on offer, and who has marked themselves into each. A
 * person in several groups shows up under each of them.
 */
function GroupEditor({ groups, colorOf, membersOf, peopleById, readOnly, onCreate, onRename, onDelete }) {
    const [newName, setNewName] = useState('');
    const add = () => {
        const name = newName.trim();
        if (!name) return;
        onCreate(name);
        setNewName('');
    };

    return (
        <div className="gathering-groups">
            {groups.map(g => {
                const ids = [...(membersOf.get(g.id) || [])];
                const people = ids.map(id => peopleById.get(id)).filter(Boolean)
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                return (
                    <div key={g.id} className="gathering-group-row" style={{ borderLeftColor: colorOf(g.id) }}>
                        <div className="gathering-group-head">
                            <GroupName value={g.name} disabled={readOnly} onSave={name => onRename(g.id, name)} />
                            <Tag variant="filled" style={{ background: colorOf(g.id), color: 'var(--bg-primary)', margin: 0 }}>
                                {ids.length} {ids.length === 1 ? 'person' : 'people'}
                            </Tag>
                            {!readOnly && (
                                <Popconfirm
                                    title="Delete this group?"
                                    description={ids.length ? `${ids.length} people picked it — their choice is removed too.` : undefined}
                                    okText="Delete"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => onDelete(g.id)}
                                >
                                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            )}
                        </div>
                        <div className="gathering-roster">
                            {people.length === 0
                                ? <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>Nobody has picked this group yet</Text>
                                : people.map(p => (
                                    <Tooltip key={p.id} title={p.name}>
                                        <span className="gathering-roster-person">
                                            <EntityImage entity={p} size={20} />
                                            <Text ellipsis style={{ maxWidth: 120, fontSize: 'var(--fs-sm)' }}>{p.name}</Text>
                                        </span>
                                    </Tooltip>
                                ))}
                        </div>
                    </div>
                );
            })}
            {!readOnly && (
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onPressEnter={add}
                        placeholder="New group, e.g. Engineers"
                        maxLength={80}
                    />
                    <Button type="dashed" icon={<PlusOutlined />} onClick={add} disabled={!newName.trim()}>
                        Add group
                    </Button>
                </Space.Compact>
            )}
        </div>
    );
}

export default GroupEditor;
